import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
    Box, Typography, Button, Card, CardContent, Grid, Chip, Divider, 
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
    Paper, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, 
    TextField, FormControl, InputLabel, Select, MenuItem, TablePagination,
    Snackbar, Alert, CircularProgress, IconButton, Tooltip, InputAdornment
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SecurityIcon from '@mui/icons-material/Security';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import UserFormModal from '../../components/UserFormModal';

const normalizeRole = (role) => {
    const roleName = typeof role === 'object' && role !== null ? role.name : role;
    return String(roleName || '').replace(/[_\s-]+/g, '').toUpperCase();
};

const isStaffBasedRoleName = (roleName) => {
    if (!roleName) return false;
    const normalized = String(roleName).replace(/[_\s-]+/g, '').toUpperCase();
    const nonStaffRoles = [
        'SUPERADMIN',
        'ADMIN',
        'HOTELADMIN',
        'APIUSER',
        'AUDITOR',
        'EXTERNALVENDOR',
        'CORPORATEADMIN'
    ];
    return !nonStaffRoles.includes(normalized);
};

const hasNoLinkedStaff = (userObj) => {
    if (!userObj) return false;
    const rName = userObj.role?.name || userObj.role || '';
    if (!isStaffBasedRoleName(rName)) {
        return false;
    }
    if (userObj.hasLinkedStaff === true || userObj.isLinkedToStaff === true || userObj.staffId || userObj.staff || userObj.linkedStaff || userObj.staffProfileId) {
        return false;
    }
    return true;
};

export default function UserDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user = useSelector(state => state.users.currentUser);
    const isLoading = useSelector(state => state.users.isLoading);
    const activityLogs = useSelector(state => state.users.userActivity) || [];
    const activityTotal = useSelector(state => state.users.userActivityTotal) || 0;
    const roles = useSelector(state => state.users.roles) || [];
    const hotels = useSelector(state => state.hotels.list) || [];
    const authUser = useSelector(state => state.auth.user);

    // Edit Modal State
    const [editModalOpen, setEditModalOpen] = useState(false);

    // Reset Password State
    const [resetDialogOpen, setResetDialogOpen] = useState(false);
    const [resetMethod, setResetMethod] = useState('manual');
    const [tempPassword, setTempPassword] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [resetStep, setResetStep] = useState(1);
    const [otp, setOtp] = useState('');
    const [resetToken, setResetToken] = useState(null);

    // MFA Toggle State
    const [mfaLoading, setMfaLoading] = useState(false);

    // Unlock State
    const [unlockLoading, setUnlockLoading] = useState(false);

    // Activity Logs Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [activityLoading, setActivityLoading] = useState(false);

    // Notification
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    const fetchActivity = useCallback(async () => {
        if (!id) return;
        setActivityLoading(true);
        try {
            await dispatch.users.fetchUserActivityAsync({ id, page, limit: rowsPerPage });
        } catch (error) {
            console.error("Error fetching activity", error);
        } finally {
            setActivityLoading(false);
        }
    }, [dispatch, id, page, rowsPerPage]);

    useEffect(() => {
        if (id) {
            dispatch.users.fetchUserByIdAsync(id);
        }
        dispatch.users.fetchRolesAsync();
        if (authUser && normalizeRole(authUser.role) === 'SUPERADMIN') {
            dispatch.hotels.fetchHotelsAsync({ noPagination: true });
        }
        return () => dispatch.users.setCurrentUser(null);
    }, [id, dispatch, authUser]);

    useEffect(() => {
        fetchActivity();
    }, [fetchActivity]);

    const closeResetDialog = () => {
        setResetDialogOpen(false);
        setTempPassword('');
        setOtp('');
        setResetStep(1);
        setResetToken(null);
        setShowPassword(false);
    };

    const openResetDialog = () => {
        setResetMethod('manual');
        setTempPassword('');
        setOtp('');
        setResetStep(1);
        setResetToken(null);
        setShowPassword(false);
        setResetDialogOpen(true);
    };

    const handleResetPassword = async () => {
        setResetLoading(true);
        try {
            await dispatch.users.resetPasswordAsync({ 
                id, 
                method: resetMethod, 
                temporaryPassword: resetMethod === 'manual' ? tempPassword : undefined 
            });
            setNotification({ 
                open: true, 
                message: resetMethod === 'email' ? 'Reset link sent to email successfully' : 'Password reset successful', 
                severity: 'success' 
            });
            closeResetDialog();
            fetchActivity(); // Refresh activity log to show the reset action
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Failed to reset password', severity: 'error' });
        } finally {
            setResetLoading(false);
        }
    };

    const handleToggleMfa = async () => {
        setMfaLoading(true);
        try {
            await dispatch.users.updateSecurityAsync({ id, mfaEnabled: !user.mfaEnabled });
            setNotification({ open: true, message: `MFA ${user.mfaEnabled ? 'disabled' : 'enabled'} successfully`, severity: 'success' });
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Failed to update MFA', severity: 'error' });
        } finally {
            setMfaLoading(false);
        }
    };

    const handleUnlockUser = async () => {
        setUnlockLoading(true);
        try {
            await dispatch.users.unlockUserAsync(id);
            setNotification({ open: true, message: 'Account unlocked successfully', severity: 'success' });
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Failed to unlock account', severity: 'error' });
        } finally {
            setUnlockLoading(false);
        }
    };

    const getChangedFields = (original, updated) => {
        const changed = {};
        Object.keys(updated).forEach((key) => {
            if (updated[key] !== original[key]) {
                changed[key] = updated[key];
            }
        });
        return changed;
    };

    const handleSaveUser = async (userData) => {
        const selectedRole = roles.find(r => r.name === userData.role);
        const payload = { ...userData };
        if (selectedRole) {
            payload.roleId = selectedRole.id;
        }

        Object.keys(payload).forEach(key => {
            if (typeof payload[key] === 'string') {
                payload[key] = payload[key].trim();
            }
            if (payload[key] === '' || payload[key] === null || payload[key] === 'null') {
                delete payload[key];
            }
        });

        try {
            const changedData = getChangedFields(user, payload);
            if (userData.createLinkedStaff !== undefined) {
                changedData.createLinkedStaff = userData.createLinkedStaff;
            }
            await dispatch.users.saveUserAsync({
                id: user.id,
                ...changedData,
            });
            // Re-fetch user details after update
            dispatch.users.fetchUserByIdAsync(id);
            setNotification({
                open: true,
                message: 'User updated successfully!',
                severity: 'success',
            });
        } catch (error) {
            setNotification({
                open: true,
                message: error.message || 'Failed to save user',
                severity: 'error',
            });
        }
    };

    if (isLoading && !user) return <Box sx={{ p: 4 }}><CircularProgress /></Box>;
    if (!user) return <Box sx={{ p: 4 }}><Typography>User not found</Typography></Box>;

    const infoItems = [
        { icon: <EmailIcon color="action" fontSize="small" />, label: "Email", value: user.email },
        { icon: <PhoneIcon color="action" fontSize="small" />, label: "Phone", value: user.phone || "N/A" },
        { icon: <BadgeIcon color="action" fontSize="small" />, label: "Employee ID", value: user.employeeId || "N/A" },
        { icon: <BusinessIcon color="action" fontSize="small" />, label: "Department", value: user.department || "N/A" },
        { icon: <CalendarTodayIcon color="action" fontSize="small" />, label: "Joining Date", value: user.joiningDate || "N/A" },
        // Temporarily hidden: MFA Status
        ...(false ? [{ icon: <SecurityIcon color="action" fontSize="small" />, label: "MFA Status", value: user.mfaEnabled ? "Enabled" : "Disabled" }] : []),
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/users')}>
                    Back to Users
                </Button>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {user.status === 'Locked' && (
                        <Button 
                            variant="contained" 
                            color="warning" 
                            startIcon={unlockLoading ? <CircularProgress size={18} color="inherit" /> : <LockOpenIcon />}
                            onClick={handleUnlockUser}
                            disabled={unlockLoading}
                        >
                            Unlock Account
                        </Button>
                    )}
                    {/* Temporarily hidden: MFA Toggle Button */}
                    {false && (
                        <Button 
                            variant="outlined" 
                            color="primary"
                            onClick={handleToggleMfa}
                            disabled={mfaLoading}
                        >
                            {mfaLoading ? <CircularProgress size={18} color="inherit" /> : (user.mfaEnabled ? 'Disable MFA' : 'Enable MFA')}
                        </Button>
                    )}
                    <Button 
                        variant="outlined" 
                        color="primary" 
                        onClick={openResetDialog}
                    >
                        Reset Password
                    </Button>
                    <Button variant="contained" color="primary" onClick={() => setEditModalOpen(true)}>Edit Profile</Button>
                </Box>
            </Box>

            <Grid container spacing={4}>
                {/* Profile Card */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', textAlign: 'center', py: 4 }}>
                        <CardContent>
                            <Avatar 
                                sx={{ width: 100, height: 100, bgcolor: 'primary.main', fontSize: 40, mx: 'auto', mb: 2 }}
                            >
                                {user.name.charAt(0)}
                            </Avatar>
                            <Typography variant="h5" fontWeight="bold">{user.name}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="body1" color="text.secondary">{user.role?.name || user.role}</Typography>
                                {hasNoLinkedStaff(user) && (
                                    <Tooltip title="Warning: This user has a staff-based role but no linked staff profile.">
                                        <WarningIcon color="warning" sx={{ fontSize: 20 }} />
                                    </Tooltip>
                                )}
                            </Box>
                            <Chip 
                                label={user.status || 'Active'} 
                                color={user.status === 'Active' ? 'success' : user.status === 'Locked' ? 'error' : 'default'} 
                                sx={{ mt: 1 }} 
                            />
                        </CardContent>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ px: 3, textAlign: 'left' }}>
                            {infoItems.map((item, idx) => (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                    {item.icon}
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">{item.label}</Typography>
                                        <Typography variant="body2" fontWeight="medium">{item.value}</Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Card>
                </Grid>

                {/* Details & Activity */}
                <Grid item xs={12} md={8}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', mb: 4 }}>
                        <CardContent>
                            <Typography variant="h6" fontWeight="bold" mb={2}>Permissions Overview</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 4 }}>
                                {((user.customPermissions?.length ? user.customPermissions : user.role?.permissions) || []).map((perm) => (
                                    <Chip key={perm} label={perm} variant="outlined" color="primary" size="small" />
                                ))}
                                {(!user.customPermissions?.length && !user.role?.permissions?.length) && (
                                    <Typography variant="body2" color="text.secondary italic">No specific permissions assigned.</Typography>
                                )}
                            </Box>

                            <Divider sx={{ mb: 4 }} />

                            <Typography variant="h6" fontWeight="bold" mb={2}>Assigned Properties</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {(user.propertyNames && user.propertyNames.length > 0) ? (
                                    user.propertyNames.map((name, idx) => (
                                        <Chip key={idx} label={name} variant="filled" color="info" size="small" />
                                    ))
                                ) : (user.assignedProperties && user.assignedProperties.length > 0) ? (
                                    user.assignedProperties.map(pId => (
                                        <Chip key={pId} label={`Property ID: ${pId}`} variant="filled" color="info" size="small" />
                                    ))
                                ) : (
                                    <Chip label={user.assignedHotel?.name || user.hotel || "N/A"} variant="filled" color="info" size="small" />
                                )}
                            </Box>
                        </CardContent>
                    </Card>

                    <Typography variant="h6" fontWeight="bold" mb={2}>Recent Activity Log</Typography>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Action</TableCell>
                                    <TableCell>Module</TableCell>
                                    <TableCell>Target</TableCell>
                                    <TableCell>Timestamp</TableCell>
                                    <TableCell>IP Address</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {activityLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell>
                                    </TableRow>
                                ) : activityLogs.length > 0 ? (
                                    activityLogs.map((log, idx) => (
                                        <TableRow key={log.id || idx}>
                                            <TableCell>{log.action}</TableCell>
                                            <TableCell>{log.module || log.action_type || 'System'}</TableCell>
                                            <TableCell>{log.target || log.resource || 'N/A'}</TableCell>
                                            <TableCell>{new Date(log.timestamp).toLocaleString()}</TableCell>
                                            <TableCell>{log.ip || log.ipAddress || 'N/A'}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" color="text.secondary">No activity logs found.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            rowsPerPageOptions={[20, 50, 100]}
                            component="div"
                            count={activityTotal}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(e, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        />
                    </TableContainer>
                </Grid>
            </Grid>

            {/* Reset Password Dialog */}
            <Dialog open={resetDialogOpen} onClose={() => !resetLoading && closeResetDialog()} maxWidth="sm" fullWidth>
                <DialogTitle>Administrative Password Reset</DialogTitle>
                <DialogContent dividers>
                    {resetStep === 1 && (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Choose a method to reset the user's password.
                            </Typography>
                            <FormControl fullWidth margin="normal" size="small">
                                <InputLabel>Reset Method</InputLabel>
                                <Select
                                    value={resetMethod}
                                    label="Reset Method"
                                    onChange={(e) => setResetMethod(e.target.value)}
                                >
                                    <MenuItem value="manual">Manual Password Entry</MenuItem>
                                    <MenuItem value="email">Send Reset Email</MenuItem>
                                </Select>
                            </FormControl>
                            {resetMethod === 'manual' && (
                                <TextField
                                    fullWidth
                                    label="Temporary Password"
                                    variant="outlined"
                                    margin="normal"
                                    size="small"
                                    type={showPassword ? 'text' : 'password'}
                                    value={tempPassword}
                                    onChange={(e) => setTempPassword(e.target.value)}
                                    required
                                    helperText="User will be required to change this password on next login."
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={closeResetDialog} disabled={resetLoading}>Cancel</Button>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={handleResetPassword}
                        disabled={
                            resetLoading || 
                            (resetMethod === 'manual' && !tempPassword)
                        }
                        startIcon={resetLoading && <CircularProgress size={18} color="inherit" />}
                    >
                        {resetMethod === 'email' ? 'Send Reset Email' : 'Confirm Reset'}
                    </Button>
                </DialogActions>
            </Dialog>

            <UserFormModal
                open={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                onSave={handleSaveUser}
                editingUser={user}
                roles={roles}
                hotels={hotels}
            />


            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
